import { getContext, select, call, take } from 'redux-saga/effects';
import { RootAction } from 'store/state';
import { TRANSITION_SUCCESS } from 'store/state/router/types';
import { Route } from 'config/routes';
import { getRouteParams } from 'store/state/selectors/router';
import { agentPageDataSelector } from 'store/state/domainData/selectors/agentById';
import { officePageDataSelector } from 'store/state/domainData/selectors/officeById';
import { IAgentPageData, IOfficeAgentDisplayLevel, IOfficePageData, LeadingAgentData, LeadingAgentFeature } from 'utils/entities';
import { SET_DOMAIN_DATA } from 'store/state/domainData/types';
import { LoadType } from '../apiService/types';
import { leadingAgentAwardDataSelector } from 'store/state/domainData/selectors/searchAgentAwardsByIds';
import { checkEnabledLeadingAgentFeature } from 'helpers/contact';


const agentPagePattern = (action: RootAction) => (
  action.type === TRANSITION_SUCCESS && action.payload.route.name === Route.AgentPage
);

export const setAgentPattern = (action: RootAction) => (
  (action.type === SET_DOMAIN_DATA && action.loadType === LoadType.AgentById)
);

export const setOfficePattern = (action: RootAction) => (
  (action.type === SET_DOMAIN_DATA && action.loadType === LoadType.OfficeById)
);

function* waitForData() {
  const agentData: IAgentPageData = yield select(agentPageDataSelector);
  const officeData: IOfficePageData = yield select(officePageDataSelector);
  const { id } = yield select(getRouteParams);

  if (!agentData || !officeData || agentData.agentId !== id) {
    yield take(setAgentPattern);
    yield take(setOfficePattern);
  }
}

export function* agentPageWorker() {
  const { sendEvent } = yield getContext('analytics');

  while (true) {
    yield take(agentPagePattern);
    yield call(waitForData);

    const { source: paramsSource } = yield select(getRouteParams);
    const agentData: IAgentPageData = yield select(agentPageDataSelector);
    const officeData: IOfficePageData = yield select(officePageDataSelector);
    const leadingAgentData: LeadingAgentData = yield select(leadingAgentAwardDataSelector);
    const isLeadingAgent = leadingAgentData.isLeadingAgent ? checkEnabledLeadingAgentFeature(leadingAgentData.award, LeadingAgentFeature.LeadingStatement) : false;
    const isPromoted = agentData.displayLevel === IOfficeAgentDisplayLevel.Premium;
    const officePhone = officeData.virtualPhone;

    const phone = officeData.officeConnectionInAgentPage || !agentData.virtualPhone ? officePhone : agentData.virtualPhone;

    const eventData = {
      agent_details: {
        id: agentData.agentId,
        name: agentData.name,
        is_free: !isPromoted,
        phone_number: phone,
        email: agentData.emailAddress,
        is_leading_agent: isLeadingAgent  ,
      },
      office_details: {
        id: officeData.officeId,
        name: officeData.officeName,
        is_free: !isPromoted,
      },
    };

    yield call(sendEvent, 'agent_page_view', 'agent', {
      ...eventData,
      event: {
        source: paramsSource || 'direct',
        display_type: isPromoted ? 'full' : 'basic',
      },
    });
  }
}
